import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import { useLayoutContext } from 'contexts/LayoutContext';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import api from 'lib/trpcClient';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import FormWrapper from './FormWrapper';
import StepAddAndAllocate from './StepAddAndAllocate';
import StepReviewAndConfirm from './StepReviewAndConfirm';

enum Steps {
  AddAndAllocate,
  ReviewAndConfirm,
}

const stepLabels = {
  [Steps.AddAndAllocate]: 'Add and allocate',
  [Steps.ReviewAndConfirm]: 'Review and confirm',
};

type Props = {
  vehicleId: string;
};

const AddEditIncomeOrExpense: React.FC<Props> = ({ vehicleId }) => {
  const { id, type } = useParams<{ id?: string; type?: IncomeOrExpenseType }>();
  const { vehicle } = useLayoutContext();
  const [activeStep, setActiveStep] = useState<Steps>(Steps.AddAndAllocate);

  const { data: incomeOrExpense, isLoading: incomeOrExpenseLoading } =
    api.incomeAndExpenses.get.useQuery(
      { vehicleId, id: id ?? '' },
      { enabled: !!id },
    );

  if (incomeOrExpenseLoading) {
    return <PageLoader />;
  }

  const title = id ? 'Edit income or expense' : 'Add new income or expense';

  return (
    <Page
      heading={title}
      goBackAction={INCOME_OR_EXPENSE_LIST}
      spacing="lg"
      withHeadingDivider
      customActions={
        <Box sx={{ width: '460px' }}>
          <Stepper activeStep={activeStep}>
            {Object.values(stepLabels).map((stepLabel) => {
              return (
                <Step key={stepLabel}>
                  <StepLabel>{stepLabel}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      }
    >
      <FormWrapper
        values={incomeOrExpense}
        vehicleId={vehicleId}
        baseCurrency={vehicle?.baseCurrency}
        defaultCategory={type}
      >
        {activeStep === Steps.AddAndAllocate && (
          <StepAddAndAllocate
            id={id}
            onNext={() => {
              setActiveStep(Steps.ReviewAndConfirm);
            }}
          />
        )}
        {activeStep === Steps.ReviewAndConfirm && !!vehicle && (
          <ErrorBoundary>
            <StepReviewAndConfirm
              id={id}
              vehicle={vehicle}
              onPreviousStep={() => {
                setActiveStep(Steps.AddAndAllocate);
              }}
            />
          </ErrorBoundary>
        )}
      </FormWrapper>
    </Page>
  );
};

const AddEditIncomeOrExpenseWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditIncomeOrExpense vehicleId={selectedVehicleId} />;
};

export default AddEditIncomeOrExpenseWrapper;
